import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SiteSettingRow {
  setting_key: string;
  setting_type: string;
  setting_value: string | null;
  setting_json: unknown;
  is_active: boolean;
}

interface UseSiteSettingsOptions {
  enabled?: boolean;
}

/**
 * Phase 7 — Site Settings hook.
 * Fetches active global settings. Always returns a `getSetting(key, fallback)`
 * helper that safely returns the fallback if the override is missing,
 * inactive, empty, or fetch fails. Never throws. Non-blocking — callers
 * render their hardcoded fallback first, then upgrade once data arrives.
 */
export function useSiteSettings({ enabled = true }: UseSiteSettingsOptions = {}) {
  const [rows, setRows] = useState<SiteSettingRow[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from("site_settings")
          .select("setting_key, setting_type, setting_value, setting_json, is_active")
          .eq("is_active", true);
        if (cancelled) return;
        if (err) {
          setError(err.message);
          setRows([]);
        } else {
          setRows((data ?? []) as unknown as SiteSettingRow[]);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Unknown error");
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const map = useMemo(() => {
    const m = new Map<string, SiteSettingRow>();
    for (const r of rows) m.set(r.setting_key, r);
    return m;
  }, [rows]);

  function getSetting(key: string, fallback: string): string {
    const r = map.get(key);
    if (!r) return fallback;
    const v = (r.setting_value ?? "").trim();
    return v.length > 0 ? v : fallback;
  }

  return { getSetting, loading, error, rows };
}

export default useSiteSettings;
